/**
 * Report document accessors — single source of truth for "which docs does
 * this report consume?". Modern reports carry `documentRefs[]` referencing
 * `site.documents` by id; legacy reports carry the docs inline on
 * `report.documents`. Callers must always go through these helpers so the
 * site-canonical migration (Documents_On_Site Prompt 1) stays clean.
 *
 * Once Prompt 2 lands AI-classified relevance + user toggles, the only
 * place that reads `documents` arrays should be this file plus the
 * AuthGate migration.
 */
import type { SiteRagReport, SiteDocument } from '../types/siteRag';
import type { SiteEntity } from '../types/site';
// DDL-FOLLOWUP-IDREF — reads through resolveSiteDocuments for fresh state
import { resolveSiteDocuments, withResolvedSiteDocumentFileName } from './site/resolveSiteDocuments';

/**
 * Returns the documents that should be considered "included" in this report:
 *  - If `documentRefs` is set, resolve each ref against `site.documents`
 *    (filtered to userIncluded === true), preserving ref order. Missing
 *    docIds are dropped silently — Documents_On_Site Prompt 1 contract.
 *  - Otherwise fall back to the legacy inline `report.documents` array.
 *
 * The `site` arg is mandatory for the modern path; passing `undefined`
 * forces the legacy fallback so unit tests can still call this without a
 * site available.
 */
export function getReportDocuments(report: SiteRagReport, site?: SiteEntity): SiteDocument[] {
  if (report.documentRefs?.length) {
    const byId = new Map(
      (site?.documents ?? []).map((d, idx) => [
        d.id,
        withResolvedSiteDocumentFileName(d, `doc-${idx}`),
      ]),
    );
    return report.documentRefs
      .filter(r => r.userIncluded)
      .map(r => byId.get(r.docId))
      .filter((d): d is SiteDocument => Boolean(d));
  }
  // DDL-FOLLOWUP-IDREF — reads through resolveSiteDocuments for fresh state.
  // When neither `documentRefs` nor the legacy snapshot carry a
  // selection, fall through the IDREF resolver so reports that only
  // stamped the new `documentIds` field (IDREF-era reports without a
  // user-curated `documentRefs` array) still surface their docs from
  // live `site.documents`. For pre-IDREF reports without `documentIds`
  // the resolver returns `report.documents ?? []`, matching the
  // historical contract exactly.
  return resolveSiteDocuments(site, report);
}
